import {
  type ChangeEvent,
  type ClipboardEvent,
  type SVGProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AsYouType,
  CountryCode,
  getCountryCallingCode,
  parseDigits,
  parsePhoneNumberFromString,
} from 'libphonenumber-js/max';
import flags from 'react-phone-number-input/flags';
import { FormControl } from '../UI-CHAKRA/FormControl';
import { Box } from '../UI-CHAKRA/Box';
import { FormLabel } from '../UI-CHAKRA/FormLabel';
import { FormErrorMessage } from '../UI-CHAKRA/FormErrorMessage';
import styled from 'styled-components';
import { themeColors } from '../../shared/theme/themeColors';
import {
  getCachedInternationalPhoneCountryConfig,
  inferInternationalPhone,
  loadInternationalPhoneCountryConfig,
  loadInternationalPhoneRulesIndex,
  matchInternationalPhoneForCountry,
} from '../../shared/internationalPhoneRules';

const flagIcons = flags as Partial<Record<string, (props: SVGProps<SVGSVGElement>) => JSX.Element>>;

const StyledPhoneInputWrapper = styled(Box)<{ $isInvalid?: boolean; $isDisabled?: boolean }>`
  .PhoneInput {
    display: flex;
    align-items: center;
    height: 40px;
    border: 1px solid ${(props) => (props.$isInvalid ? themeColors.error.main : '#CBBFE6')};
    border-radius: 10px;
    background-color: #fff;
    padding-left: 12px;
    padding-right: 12px;
    transition: border 0.3s;
    ${(props) =>
      props.$isDisabled &&
      `
      opacity: 0.4;
      cursor: not-allowed;
    `}

    &:focus-within {
      border-color: ${(props) => (props.$isInvalid ? themeColors.error.main : '#3D0E6B')};
      box-shadow: ${(props) => (props.$isInvalid ? `0 0 0 1px ${themeColors.error.main}` : '0 0 0 1px #3D0E6B')};
    }
  }

  ${(props) =>
    props.$isDisabled &&
    `
    .PhoneInputCountrySelect {
      cursor: not-allowed;
    }

    .PhoneInputInput {
      cursor: not-allowed;
    }
  `}

  .PhoneInputCountry {
    position: relative;
    display: flex;
    align-items: center;
    margin-right: 8px;
    padding-right: 8px;
    border-right: 1px solid ${themeColors.gray.light};
  }

  .PhoneInputCountryIcon {
    width: 28px;
    height: 20px;
    border-radius: 2px;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
  }

  .PhoneInputCountryIcon--border {
    box-shadow: none;
  }

  .PhoneInputCountrySelectArrow {
    rotate: 945deg;
    display: block;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 4px 3px 0 3px;
    border-color: #3d0e6b transparent transparent transparent;
    margin-left: 6px;
    opacity: 0.7;
  }

  .PhoneInputCountrySelect {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    z-index: 1;
    border: 0;
    opacity: 0;
    cursor: pointer;
  }

  .PhoneInputInput {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: none;
    outline: none;
    background-color: transparent;
    font-size: 14px;
    font-family: 'Ambit-Regular', 'Inter', sans-serif;
    color: ${themeColors.typography.darkest};
    padding: 0;
    font-weight: 400;

    &::placeholder {
      color: ${themeColors.typography.darkest};
      opacity: 0.5;
    }
  }

  .PhoneInputPrefix {
    color: ${themeColors.typography.darkest};
    font-family: 'Ambit-Regular', 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 400;
    margin-right: 8px;
    white-space: nowrap;
  }

  .PhoneInputInput:-webkit-autofill {
    animation-duration: 0.01s;
    animation-name: phone-input-autofill;
  }

  @keyframes phone-input-autofill {
    from {
      opacity: 1;
    }

    to {
      opacity: 1;
    }
  }
`;

type InjectionSource = 'autofill' | 'paste';
type BlurCommitReason = 'keyboard-tab' | 'pointer-outside';

interface CountryOption {
  ddi: string;
  label: string;
  value: CountryCode;
}

interface PhoneInputProps {
  labelText?: string;
  value: string;
  onChange: (value: string | undefined) => void;
  onBlur?: () => void;
  errorText?: string;
  isInvalid?: boolean;
  placeholder?: string;
  defaultCountry?: string;
  inputTestId?: string;
  disabled?: boolean;
}

function buildCountrySuggestionError(
  selectedCountryName: string | undefined,
  rawSuggestions: readonly { countryName: string; ddi: string }[]
): string {
  const suggestions = rawSuggestions.slice(0, 3).map((suggestion) => `${suggestion.countryName} (+${suggestion.ddi})`);
  const invalidForSelectedCountryText = selectedCountryName
    ? `Número não é válido considerando ${selectedCountryName} como país selecionado.`
    : 'Número parece corresponder a outro país.';

  if (!suggestions.length) {
    return invalidForSelectedCountryText;
  }

  return `${invalidForSelectedCountryText} Sugestões: ${suggestions.join(', ')}.`;
}

function isGenericInvalidPhoneError(errorText?: string): boolean {
  return errorText === 'Informe um número de telefone válido.' || errorText === 'Informe um número de celular válido.';
}

function buildSelectedCountryInvalidError(errorText: string, countryName?: string): string {
  if (!countryName || !isGenericInvalidPhoneError(errorText)) {
    return errorText;
  }

  if (errorText === 'Informe um número de celular válido.') {
    return `Informe um número de celular válido considerando ${countryName} como país selecionado.`;
  }

  return `Informe um número de telefone válido considerando ${countryName} como país selecionado.`;
}

function buildPhoneInvalidError(
  errorText: string,
  countryName: string | undefined,
  shouldPersonalize: boolean
): string {
  return shouldPersonalize ? buildSelectedCountryInvalidError(errorText, countryName) : errorText;
}

// Mantém no input apenas dígitos e um único + opcional na primeira posição.
function sanitizeUserPhoneInput(rawValue: string): string {
  const trimmedValue = rawValue.trim();
  const digitsOnly = parseDigits(trimmedValue);

  return trimmedValue.startsWith('+') ? `+${digitsOnly}` : digitsOnly;
}

function normalizePhoneInputForParsing(value: string): string {
  const trimmedValue = value.trim();

  if (!trimmedValue.startsWith('00')) {
    return value;
  }

  const digits = parseDigits(trimmedValue);

  if (!digits.startsWith('00')) {
    return value;
  }

  return `+${digits.slice(2)}`;
}

function hasExplicitInternationalPrefix(value: string): boolean {
  const trimmedValue = value.trim();
  return trimmedValue.startsWith('+') || trimmedValue.startsWith('00');
}

// Converte o valor bruto para a parte nacional visível do país selecionado.
// Se a entrada já vier com o mesmo DDI explícito do país atual, o DDI é removido
// da parte editável para evitar duplicidade com o prefixo visual do componente.
function formatDisplayValueForCountry(rawValue: string, countryCode: CountryCode): string {
  const digits = parseDigits(rawValue);
  const trimmedValue = rawValue.trim();
  const countryDdi = getCountryCallingCode(countryCode);
  const digitsWithoutSelectedDdi =
    (trimmedValue.startsWith('+') || trimmedValue.startsWith('00')) && digits.startsWith(countryDdi)
      ? digits.slice(countryDdi.length)
      : digits;

  if (!digitsWithoutSelectedDdi) {
    return '';
  }

  return new AsYouType(countryCode).input(digitsWithoutSelectedDdi);
}

// Quando o campo já estava preenchido e o usuário entra para editar,
// o DDI volta para dentro do input como se tivesse sido digitado manualmente.
function buildEditablePhoneValue(rawValue: string, countryCode: CountryCode): string {
  const sanitizedValue = sanitizeUserPhoneInput(rawValue);
  const trimmedValue = sanitizedValue.trim();

  if (!trimmedValue) {
    return '';
  }

  if (trimmedValue.startsWith('+')) {
    return trimmedValue;
  }

  return `+${getCountryCallingCode(countryCode)}${trimmedValue}`;
}

function CountrySelect({
  disabled,
  onChange,
  options,
  selectedCountryCode,
}: {
  disabled?: boolean;
  onChange: (countryCode: CountryCode) => void;
  options: readonly CountryOption[];
  selectedCountryCode: CountryCode;
}) {
  const selectedCountry = options.find((option) => option.value === selectedCountryCode);
  const SelectedFlag = flagIcons[selectedCountryCode];

  return (
    <div className="PhoneInputCountry">
      <select
        aria-label="País"
        className="PhoneInputCountrySelect"
        disabled={disabled}
        value={selectedCountryCode}
        onChange={(event) => onChange(event.target.value as CountryCode)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {SelectedFlag && selectedCountry ? (
        <SelectedFlag aria-hidden className="PhoneInputCountryIcon" />
      ) : (
        <div className="PhoneInputCountryIcon PhoneInputCountryIcon--border" />
      )}
      <div className="PhoneInputCountrySelectArrow" />
    </div>
  );
}

export function PhoneInput({
  labelText,
  value,
  onChange,
  onBlur,
  errorText,
  isInvalid,
  placeholder = 'Digite o telefone',
  defaultCountry = 'BR',
  inputTestId,
  disabled,
}: PhoneInputProps) {
  const defaultCountryCode = defaultCountry as CountryCode;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const blurCommitReasonRef = useRef<BlurCommitReason | null>(null);
  const hasHandledInitialExternalValueRef = useRef(false);
  const inferenceRequestIdRef = useRef(0);
  const inputValueRef = useRef('');
  const isFocusedRef = useRef(false);
  const lastAppliedNormalizedValueRef = useRef<string | null>(null);
  const lastObservedPropValueRef = useRef(value);
  const lastRawInputValueRef = useRef('');
  const manualInputSinceFocusRef = useRef(false);
  const pendingBlurCommitRef = useRef(false);
  const pendingInjectionSourceRef = useRef<InjectionSource | null>(null);
  const skipNextExternalSyncValueRef = useRef<string | null>(null);
  const [countryConfig, setCountryConfig] = useState(() => getCachedInternationalPhoneCountryConfig());
  const [displayValue, setDisplayValue] = useState('');
  const [hasExplicitInternationalIntent, setHasExplicitInternationalIntent] = useState(false);
  const [internalErrorText, setInternalErrorText] = useState<string>();
  const [isLoadingCountryConfig, setIsLoadingCountryConfig] = useState(!countryConfig);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [keepDdiPrefixVisible, setKeepDdiPrefixVisible] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState<CountryCode>(defaultCountryCode);

  useEffect(() => {
    let isMounted = true;

    if (countryConfig) {
      setIsLoadingCountryConfig(false);
      return () => {
        isMounted = false;
      };
    }

    loadInternationalPhoneCountryConfig().then((loadedCountryConfig) => {
      if (!isMounted) {
        return;
      }

      setCountryConfig(loadedCountryConfig);
      setIsLoadingCountryConfig(false);
    });

    return () => {
      isMounted = false;
    };
  }, [countryConfig]);

  const countryOptions = useMemo<readonly CountryOption[]>(() => {
    if (!countryConfig) {
      return [];
    }

    return countryConfig.countryCodes.map((countryCode) => ({
      ddi: getCountryCallingCode(countryCode as CountryCode),
      label: countryConfig.labels[countryCode],
      value: countryCode as CountryCode,
    }));
  }, [countryConfig]);

  // Aplica o valor normalizado no contrato final do componente:
  // país selecionado correto, E.164 no onChange e input exibindo só a parte nacional.
  const applyNormalizedPhone = useCallback(
    (normalizedPhone: string, countryCode: CountryCode, nationalNumber: string) => {
      const formattedDisplayValue = formatDisplayValueForCountry(nationalNumber, countryCode);

      lastAppliedNormalizedValueRef.current = normalizedPhone;
      inputValueRef.current = formattedDisplayValue;
      lastRawInputValueRef.current = formattedDisplayValue;
      setDisplayValue(formattedDisplayValue);
      setSelectedCountryCode(countryCode);
      setInternalErrorText(undefined);
      onChange(normalizedPhone);
    },
    [onChange]
  );

  // Atualiza apenas a parte visível do input com a máscara do país selecionado.
  const applyDisplayMaskForCountry = useCallback((rawValue: string, countryCode: CountryCode) => {
    const formattedDisplayValue = formatDisplayValueForCountry(rawValue, countryCode);

    inputValueRef.current = formattedDisplayValue;
    lastRawInputValueRef.current = formattedDisplayValue;
    setDisplayValue(formattedDisplayValue);
  }, []);

  // Quando o país muda via combobox, a regra é revalidar no país escolhido
  // sem trocar silenciosamente para outro país e sem sobrescrever o valor bruto digitado.
  // Números internacionais explícitos mantêm a intenção original do usuário.
  const revalidateWithSelectedCountry = useCallback(
    async (rawValue: string, countryCode: CountryCode) => {
      const trimmedValue = rawValue.trim();

      if (!trimmedValue) {
        setInternalErrorText(undefined);
        return;
      }

      if (hasExplicitInternationalIntent) {
        setInternalErrorText(undefined);
        return;
      }

      const requestId = ++inferenceRequestIdRef.current;
      const rulesIndex = await loadInternationalPhoneRulesIndex();

      if (requestId !== inferenceRequestIdRef.current) {
        return;
      }

      const countryName = countryConfig?.labels[countryCode];
      const selectedCountryCandidate = matchInternationalPhoneForCountry(rulesIndex, trimmedValue, countryCode);

      if (!selectedCountryCandidate) {
        applyDisplayMaskForCountry(trimmedValue, countryCode);
        setInternalErrorText(buildPhoneInvalidError('Informe um número de telefone válido.', countryName, true));
        return;
      }

      setInternalErrorText(undefined);
    },
    [applyDisplayMaskForCountry, countryConfig, hasExplicitInternationalIntent]
  );

  // Centraliza os três fluxos de negócio do componente:
  // `injected`: autoaplica o melhor país quando o valor entra de uma vez.
  // `manual-blur`: valida no blur e, se divergir do país selecionado, sugere alternativas.
  // `autofill`: reaproveita a lógica do blur, mas mantém o DDI visível mesmo com foco.
  const normalizeWithInference = useCallback(
    async (rawValue: string, mode: 'injected' | 'manual-blur' | 'autofill') => {
      const trimmedValue = rawValue.trim();

      if (!trimmedValue) {
        setInternalErrorText(undefined);
        return;
      }

      const requestId = ++inferenceRequestIdRef.current;
      const rulesIndex = await loadInternationalPhoneRulesIndex();

      if (requestId !== inferenceRequestIdRef.current) {
        return;
      }

      const selectedCountryName = countryConfig?.labels[selectedCountryCode];
      const selectedCountryCandidate = matchInternationalPhoneForCountry(rulesIndex, trimmedValue, selectedCountryCode);

      if (selectedCountryCandidate) {
        applyNormalizedPhone(
          selectedCountryCandidate.e164,
          selectedCountryCode,
          selectedCountryCandidate.nationalNumber
        );
        return;
      }

      const inference = inferInternationalPhone(rulesIndex, trimmedValue);
      const topCandidate = inference.topCandidate;
      const shouldBehaveLikeBlur = mode === 'manual-blur' || mode === 'autofill';
      const hasExplicitDdi = hasExplicitInternationalPrefix(trimmedValue);
      const hasUsableExplicitCandidate = !!topCandidate && topCandidate.score > 0;

      if (!topCandidate) {
        if (shouldBehaveLikeBlur) {
          if (hasExplicitDdi) {
            inputValueRef.current = trimmedValue;
            lastRawInputValueRef.current = trimmedValue;
            setDisplayValue(trimmedValue);
          } else {
            applyDisplayMaskForCountry(trimmedValue, selectedCountryCode);
          }
        }

        setInternalErrorText(
          shouldBehaveLikeBlur
            ? buildPhoneInvalidError('Informe um número de telefone válido.', selectedCountryName, !hasExplicitDdi)
            : undefined
        );
        return;
      }

      if (hasExplicitDdi) {
        if (!hasUsableExplicitCandidate) {
          inputValueRef.current = trimmedValue;
          lastRawInputValueRef.current = trimmedValue;
          setDisplayValue(trimmedValue);
          setSelectedCountryCode(topCandidate.countryCode as CountryCode);
          setInternalErrorText(shouldBehaveLikeBlur ? 'Informe um número de telefone válido.' : undefined);
          return;
        }

        applyNormalizedPhone(topCandidate.e164, topCandidate.countryCode as CountryCode, topCandidate.nationalNumber);
        return;
      }

      if (shouldBehaveLikeBlur) {
        applyDisplayMaskForCountry(trimmedValue, selectedCountryCode);

        if (topCandidate.countryCode !== selectedCountryCode) {
          setInternalErrorText(
            buildCountrySuggestionError(
              selectedCountryName,
              inference.candidates.map((candidate) => ({
                countryName: candidate.countryName,
                ddi: candidate.ddi,
              }))
            )
          );
          return;
        }

        setInternalErrorText(
          buildSelectedCountryInvalidError('Informe um número de telefone válido.', selectedCountryName)
        );
        return;
      }

      applyNormalizedPhone(topCandidate.e164, topCandidate.countryCode as CountryCode, topCandidate.nationalNumber);
    },
    [applyDisplayMaskForCountry, applyNormalizedPhone, countryConfig, selectedCountryCode]
  );

  // Sincroniza valores externos, inclusive prefill, sem sobrescrever a digitação em andamento.
  const syncExternalValue = useCallback(
    (nextValue: string) => {
      if (!nextValue.trim()) {
        inputValueRef.current = '';
        lastRawInputValueRef.current = '';
        setDisplayValue('');
        setSelectedCountryCode(defaultCountryCode);
        setHasExplicitInternationalIntent(false);
        setInternalErrorText(undefined);
        lastAppliedNormalizedValueRef.current = null;
        return;
      }

      const shouldPreserveExplicitIntent = nextValue === lastAppliedNormalizedValueRef.current;
      const nextHasExplicitInternationalIntent = shouldPreserveExplicitIntent
        ? hasExplicitInternationalIntent
        : hasExplicitInternationalPrefix(nextValue);
      const parsedPhoneNumber = parsePhoneNumberFromString(
        normalizePhoneInputForParsing(nextValue),
        selectedCountryCode
      );

      if (
        nextValue === lastAppliedNormalizedValueRef.current ||
        nextValue.trim().startsWith('+') ||
        nextValue.trim().startsWith('00')
      ) {
        const hasUsableParsedPhoneNumber =
          !!parsedPhoneNumber && (parsedPhoneNumber.isValid() || parsedPhoneNumber.isPossible());
        const nextCountryCode = hasUsableParsedPhoneNumber
          ? ((parsedPhoneNumber?.country as CountryCode | undefined) ?? selectedCountryCode)
          : selectedCountryCode;
        const nextDisplayValue = hasUsableParsedPhoneNumber
          ? (parsedPhoneNumber?.formatNational() ?? nextValue)
          : nextValue;

        inputValueRef.current = nextDisplayValue;
        lastRawInputValueRef.current = nextDisplayValue;
        setHasExplicitInternationalIntent(nextHasExplicitInternationalIntent);
        setDisplayValue(nextDisplayValue);
        setSelectedCountryCode(nextCountryCode);
        return;
      }

      setHasExplicitInternationalIntent(nextHasExplicitInternationalIntent);
      void normalizeWithInference(nextValue, 'injected');
    },
    [defaultCountryCode, hasExplicitInternationalIntent, normalizeWithInference, selectedCountryCode]
  );

  useEffect(() => {
    if (!countryConfig) {
      return;
    }

    if (!hasHandledInitialExternalValueRef.current) {
      hasHandledInitialExternalValueRef.current = true;
      lastObservedPropValueRef.current = value;
      syncExternalValue(value);
      return;
    }

    if (value === lastObservedPropValueRef.current) {
      return;
    }

    lastObservedPropValueRef.current = value;

    if (skipNextExternalSyncValueRef.current !== null && value === skipNextExternalSyncValueRef.current) {
      skipNextExternalSyncValueRef.current = null;
      return;
    }

    if (isFocusedRef.current) {
      return;
    }

    syncExternalValue(value);
  }, [countryConfig, syncExternalValue, value]);

  const commitBlur = useCallback(() => {
    pendingBlurCommitRef.current = false;
    blurCommitReasonRef.current = null;
    isFocusedRef.current = false;
    setIsInputFocused(false);
    setKeepDdiPrefixVisible(false);

    const rawValue = inputValueRef.current;
    const shouldNormalizeManualInput = manualInputSinceFocusRef.current && !!rawValue.trim();

    manualInputSinceFocusRef.current = false;
    pendingInjectionSourceRef.current = null;

    if (!shouldNormalizeManualInput) {
      if (rawValue.trim()) {
        applyDisplayMaskForCountry(rawValue, selectedCountryCode);
      }

      onBlur?.();
      return;
    }

    void normalizeWithInference(rawValue, 'manual-blur').finally(() => {
      onBlur?.();
    });
  }, [applyDisplayMaskForCountry, normalizeWithInference, onBlur, selectedCountryCode]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      const isInsideControl = !!(target && rootRef.current?.contains(target));

      if (pendingBlurCommitRef.current) {
        if (!isInsideControl) {
          commitBlur();
        }

        return;
      }

      if (!isFocusedRef.current) {
        return;
      }

      blurCommitReasonRef.current = isInsideControl ? null : 'pointer-outside';
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (pendingBlurCommitRef.current) {
        if (event.key === 'Tab') {
          commitBlur();
        }

        return;
      }

      if (!isFocusedRef.current) {
        return;
      }

      blurCommitReasonRef.current = event.key === 'Tab' ? 'keyboard-tab' : null;
    };

    document.addEventListener('mousedown', handlePointerDown, true);
    document.addEventListener('touchstart', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true);
      document.removeEventListener('touchstart', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [commitBlur]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const rawValue = sanitizeUserPhoneInput(event.currentTarget.value);
      const nativeEvent = event.nativeEvent as InputEvent & { inputType?: string };
      const previousDigitCount = parseDigits(lastRawInputValueRef.current).length;
      const nextDigitCount = parseDigits(rawValue).length;
      const isBulkDigitInsertion = nextDigitCount - previousDigitCount >= 6;
      const isInjectedInput =
        !!pendingInjectionSourceRef.current ||
        nativeEvent.inputType === 'insertFromPaste' ||
        nativeEvent.inputType === 'insertReplacementText' ||
        (!manualInputSinceFocusRef.current && isBulkDigitInsertion);

      setInternalErrorText(undefined);
      setKeepDdiPrefixVisible(false);

      if (!rawValue.trim()) {
        inputValueRef.current = '';
        lastRawInputValueRef.current = '';
        manualInputSinceFocusRef.current = false;
        pendingInjectionSourceRef.current = null;
        lastAppliedNormalizedValueRef.current = null;
        setHasExplicitInternationalIntent(false);
        setDisplayValue('');
        setSelectedCountryCode(defaultCountryCode);
        onChange('');
        return;
      }

      // Entradas "grandes" vindas de paste/preenchimento automático entram direto
      // no fluxo de inferência; digitação normal só é analisada no blur.
      if (isInjectedInput) {
        inputValueRef.current = rawValue;
        lastRawInputValueRef.current = rawValue;
        manualInputSinceFocusRef.current = false;
        pendingInjectionSourceRef.current = null;
        setHasExplicitInternationalIntent(hasExplicitInternationalPrefix(rawValue));
        setDisplayValue(rawValue);
        onChange(rawValue);
        void normalizeWithInference(rawValue, 'injected');
        return;
      }

      inputValueRef.current = rawValue;
      lastRawInputValueRef.current = rawValue;
      manualInputSinceFocusRef.current = true;
      pendingInjectionSourceRef.current = null;
      setHasExplicitInternationalIntent(hasExplicitInternationalPrefix(rawValue));
      setDisplayValue(rawValue);
      onChange(rawValue);
    },
    [defaultCountryCode, normalizeWithInference, onChange]
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const nextFocusedElement = event.relatedTarget as Node | null;
      const isMovingFocusInsideControl = !!(nextFocusedElement && rootRef.current?.contains(nextFocusedElement));

      if (isMovingFocusInsideControl) {
        blurCommitReasonRef.current = null;
        pendingBlurCommitRef.current = false;
        return;
      }

      if (!blurCommitReasonRef.current) {
        pendingBlurCommitRef.current = true;
        return;
      }

      commitBlur();
    },
    [commitBlur]
  );

  const handleFocus = useCallback(() => {
    pendingBlurCommitRef.current = false;
    blurCommitReasonRef.current = null;
    isFocusedRef.current = true;
    setIsInputFocused(true);
    setKeepDdiPrefixVisible(false);
    manualInputSinceFocusRef.current = false;
    pendingInjectionSourceRef.current = null;
    setInternalErrorText(undefined);

    if (!inputValueRef.current.trim()) {
      return;
    }

    const editableValue = buildEditablePhoneValue(inputValueRef.current, selectedCountryCode);

    inputValueRef.current = editableValue;
    lastRawInputValueRef.current = editableValue;
    setDisplayValue(editableValue);
  }, [selectedCountryCode]);

  const handleCountryChange = useCallback(
    (countryCode: CountryCode) => {
      const nextDisplayValue = inputValueRef.current;

      setSelectedCountryCode(countryCode);
      setInternalErrorText(undefined);
      inputValueRef.current = nextDisplayValue;
      lastRawInputValueRef.current = nextDisplayValue;
      setDisplayValue(nextDisplayValue);
      manualInputSinceFocusRef.current = !!nextDisplayValue.trim();

      if (!nextDisplayValue.trim()) {
        onChange('');
        return;
      }

      lastAppliedNormalizedValueRef.current = null;
      skipNextExternalSyncValueRef.current = nextDisplayValue;
      onChange(nextDisplayValue);
      void revalidateWithSelectedCountry(nextDisplayValue, countryCode);
    },
    [onChange, revalidateWithSelectedCountry]
  );

  const handleAutofill = useCallback(() => {
    const rawValue = sanitizeUserPhoneInput(inputRef.current?.value ?? '');

    pendingInjectionSourceRef.current = 'autofill';
    inputValueRef.current = rawValue;
    lastRawInputValueRef.current = rawValue;

    if (!rawValue.trim()) {
      return;
    }

    // Mesmo com o input ainda focado, o autofill deve se comportar visualmente
    // como o blur: DDI visível fora do input e número já mascarado.
    setKeepDdiPrefixVisible(true);
    applyDisplayMaskForCountry(rawValue, selectedCountryCode);
    onChange(rawValue);
    void normalizeWithInference(rawValue, 'autofill');
  }, [applyDisplayMaskForCountry, normalizeWithInference, onChange, selectedCountryCode]);

  const handleAnimationStart = useCallback(
    (event: React.AnimationEvent<HTMLInputElement>) => {
      if (event.animationName !== 'phone-input-autofill') {
        return;
      }

      window.setTimeout(() => {
        handleAutofill();
      }, 0);
    },
    [handleAutofill]
  );

  const handlePaste = useCallback((_event: ClipboardEvent<HTMLInputElement>) => {
    pendingInjectionSourceRef.current = 'paste';
  }, []);

  const selectedCountryName = countryConfig?.labels[selectedCountryCode];
  const selectedCountryDdi = useMemo(() => getCountryCallingCode(selectedCountryCode), [selectedCountryCode]);
  const shouldShowDdiPrefix =
    !!displayValue.trim() && (!isInputFocused || keepDdiPrefixVisible) && !hasExplicitInternationalPrefix(displayValue);
  const externalErrorText =
    errorText && displayValue.trim()
      ? buildPhoneInvalidError(errorText, selectedCountryName, !hasExplicitInternationalIntent)
      : errorText;
  const effectiveErrorText = internalErrorText || externalErrorText;
  const effectiveIsInvalid = Boolean(effectiveErrorText) || isInvalid;

  return (
    <FormControl isInvalid={effectiveIsInvalid}>
      {labelText && <FormLabel>{labelText}</FormLabel>}
      <StyledPhoneInputWrapper $isInvalid={effectiveIsInvalid} $isDisabled={disabled}>
        <div className="PhoneInput" ref={rootRef} data-testid={inputTestId ? `${inputTestId}-wrapper` : undefined}>
          <CountrySelect
            disabled={disabled || isLoadingCountryConfig}
            onChange={handleCountryChange}
            options={countryOptions}
            selectedCountryCode={selectedCountryCode}
          />
          {shouldShowDdiPrefix && (
            <span className="PhoneInputPrefix" data-testid={inputTestId ? `${inputTestId}-ddi` : undefined}>
              +{selectedCountryDdi}
            </span>
          )}
          <input
            ref={inputRef}
            autoComplete="tel"
            className="PhoneInputInput"
            data-testid={inputTestId}
            disabled={disabled || isLoadingCountryConfig}
            placeholder={placeholder}
            type="tel"
            value={displayValue}
            onAnimationStart={handleAnimationStart}
            onBlur={handleBlur}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onPaste={handlePaste}
          />
        </div>
      </StyledPhoneInputWrapper>
      {effectiveErrorText && <FormErrorMessage>{effectiveErrorText}</FormErrorMessage>}
    </FormControl>
  );
}
